import type { MapartDocument } from './document';
import type { MapartSettings } from './types';
import type { Viewport } from './viewport';

export const PROJECT_FILE_VERSION = 1;
export const PROJECT_FILE_EXTENSION = '.mapart.json';

export interface MapartProject {
  id: string;
  name: string;
  updatedAt: string;
  doc: MapartDocument;
  settings: MapartSettings;
  /** Layer source id -> the original image bytes, so a reopened project still has its pictures. */
  images: Record<string, Blob>;
  /** Where the user left the view. Outside the document on purpose: panning is not an edit, so it
   * must not enter the undo history or count as a change the build has to react to. */
  viewport?: Viewport;
}

export interface ProjectSummary {
  id: string;
  name: string;
  updatedAt: string;
}

interface ProjectFile {
  version: number;
  name: string;
  updatedAt: string;
  doc: MapartDocument;
  settings: MapartSettings;
  images: Record<string, { type: string; data: string }>;
  viewport?: Viewport;
}

export function newProjectId(): string {
  return crypto.randomUUID();
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function fromBase64(data: string): Uint8Array {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function serialiseProject(project: MapartProject): Promise<string> {
  const images: ProjectFile['images'] = {};
  for (const [sourceId, blob] of Object.entries(project.images)) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    images[sourceId] = { type: blob.type || 'image/png', data: toBase64(bytes) };
  }
  const file: ProjectFile = {
    version: PROJECT_FILE_VERSION,
    name: project.name,
    updatedAt: project.updatedAt,
    doc: project.doc,
    settings: project.settings,
    images,
    viewport: project.viewport,
  };
  return JSON.stringify(file);
}

export function parseProject(text: string, id: string): MapartProject {
  const file = JSON.parse(text) as ProjectFile;
  if (file.version !== PROJECT_FILE_VERSION) {
    throw new Error(`Unsupported project version ${file.version}`);
  }
  if (!file.doc || !Array.isArray(file.doc.layers) || !file.settings) {
    throw new Error('Project file is missing a document or settings');
  }
  const images: Record<string, Blob> = {};
  for (const [sourceId, entry] of Object.entries(file.images ?? {})) {
    images[sourceId] = new Blob([fromBase64(entry.data) as BlobPart], { type: entry.type });
  }
  return {
    id,
    name: file.name,
    updatedAt: file.updatedAt,
    doc: file.doc,
    settings: file.settings,
    images,
    viewport: file.viewport,
  };
}

/** Highest `l<n>` suffix in the document, so reopening cannot hand out ids that already exist. */
export function highestLayerCounter(doc: MapartDocument): number {
  let highest = 0;
  for (const layer of doc.layers) {
    for (const id of [layer.id, layer.kind === 'image' ? layer.sourceId : '']) {
      const match = /^l(\d+)$/.exec(id);
      if (match) highest = Math.max(highest, Number(match[1]));
    }
  }
  return highest;
}
